// ═══════════════════════════════════════════════════════════════
// PlaceholderDashboard.jsx — verticalstack section dispatcher
// Path: src/components/PlaceholderDashboard.jsx
//
// S31 LAYER 1 (Apr 27, 2026) — NEW FILE for sandbox-schema migration.
// S32 LAYER 2 OVERVIEW (Apr 28, 2026) — section dispatch added.
// S33 LAYER 2 PORTFOLIO (Apr 28, 2026) — second dispatcher branch.
// S34 LAYER 2 OPERATE-A (Apr 28, 2026) — third dispatcher branch.
// S36 LAYER 2 MAINTAIN (Apr 28, 2026) — fourth dispatcher branch.
// S3.C SMOKE HARNESS (May 18, 2026) — XC-DPUI smoke render at top.
// S4.C SMOKE HARNESS (May 18, 2026) — XC-FILTER smoke render added
//                                      alongside DPUI harness.
// S5.B XC-DOC DISPATCH (May 18, 2026) — Documents import, xcdoc-smoke
//                                        harness, xc-documents dispatch.
// S6.B XC-MSG DISPATCH (May 19, 2026) — Messages import, xcmsg-smoke
//                                        harness (4th harness), xc-messages
//                                        dispatch (6th case).
// S7.A XC-TEAM DISPATCH (May 21, 2026) — Team import, xcteam-smoke
//                                         harness (5th harness), xc-team
//                                         dispatch (7th case).
// S7.B XC-REV HARNESS (May 21, 2026) — RevPlanPanel import, revplan-smoke
//                                       harness (6th harness). No live
//                                       dispatch case — panel triggers
//                                       from stage views, not from a
//                                       left-nav section.
// S8.B XC-NET DISPATCH (Jun 29, 2026) — Network import, xcnet-smoke
//                                        harness (7th harness), xc-network
//                                        dispatch (4th cross-cutting case,
//                                        8th case overall). Fourth unlocked
//                                        Cross-cutting nav item.
// S9.A XC-MKT DISPATCH (Jun 30, 2026) — Marketplace import, xcmkt-smoke
//                                        harness (8th harness), xc-marketplace
//                                        dispatch (5th cross-cutting case,
//                                        9th case overall). Fifth unlocked
//                                        Cross-cutting nav item.
// S10.B XC-CAL DISPATCH (Jul 8, 2026) — Calendar import, xccal-smoke
//                                        harness (9th harness), xc-calendar
//                                        dispatch (6th cross-cutting case,
//                                        10th case overall). Sixth unlocked
//                                        Cross-cutting nav item.
//
// PURPOSE
//   Dispatched from Shell.jsx based on currentSection state. As Layer 2+
//   absorption lands real components, each section is replaced inline here
//   instead of editing Shell. This keeps Shell as pure layout and section
//   routing in one place.
//
// SMOKE HARNESSES (9)
//   Nine opt-in query-param harnesses run before section dispatch. All
//   intercept render when present; absent, normal section routing applies.
//
//   ?dpui-smoke=1     → renders <DiscreteProcessUI> with businessCase config
//                       (S3.C harness, May 18, 2026)
//   ?xcfilter-smoke=1 → renders <XcFilterBar viewId="opr-props"> inside
//                       a dev wrapper showing the filter chip row in
//                       isolation (S4.C harness, May 18, 2026)
//   ?xcdoc-smoke=1    → renders <Documents mockMode={true}> with hardcoded
//                       4-doc mock array spanning stages
//                       (S5.B harness, May 18, 2026)
//   ?xcmsg-smoke=1    → renders <Messages mockMode={true}> with hardcoded
//                       6-thread mock array spanning 4 status states
//                       (S6.B harness, May 19, 2026)
//   ?xcteam-smoke=1   → renders <Team mockMode={true}> with hardcoded
//                       5-member mock array on workspace a1, exercising
//                       compound owner_pm role rendering (S7.A harness,
//                       May 21, 2026)
//   ?revplan-smoke=1  → renders <RevPlanPanel mockMode={true}> in inline
//                       wrapper, exercises two-tab structure + 7 chapter
//                       placeholders + Ledger tab (S7.B harness,
//                       May 21, 2026)
//   ?xcnet-smoke=1    → renders <Network mockMode={true}> with hardcoded
//                       8-vendor mock array spanning T1/T2/T3, exercising
//                       tier grouping, tier+category filter, Trustack
//                       signals card, and the vendor table (S8.B harness,
//                       Jun 29, 2026)
//   ?xcmkt-smoke=1    → renders <Marketplace mockMode={true}> with hardcoded
//                       6-vendor mock array spanning T1/T2/T3, exercising
//                       the search form, client-side matchVendors ranking,
//                       T1/T2/T3 candidate cards, tier post-filter, and the
//                       T3 fallback band (S9.A harness, Jun 30, 2026)
//   ?xccal-smoke=1    → renders <Calendar mockMode={true}> with hardcoded
//                       8-event mock array spanning event types, exercising
//                       the upcoming-events table, the four KPI cards
//                       (Upcoming / Next 30 / Unassigned / iCal stub), the
//                       iCal band, and the + New event form (disabled in
//                       mock) (S10.B harness, Jul 8, 2026)
//
//   Smoke harnesses sit at the TOP of the dispatcher function (before
//   section routing) and check window.location.search directly to avoid
//   coupling to react-router. To remove any harness, delete its
//   if-block plus its imports at the top of the file.
//
// LAYER 2 DISPATCH (sections absorbed)
//   overview       → <Overview />
//   portfolio      → <Portfolio />
//   operate        → <Operate />   (STR | LTR + Messages + Assets internal tabs)
//   maintain       → <Maintain />  (7 internal tabs, absorbs 4 PMApp routes)
//   xc-network     → <Network />     (S8.B — XC-NET, 4th unlocked Cross-cutting)
//   xc-marketplace → <Marketplace /> (S9.A — XC-MKT, 5th unlocked Cross-cutting)
//   xc-calendar    → <Calendar />    (S10.B — XC-CAL, 6th unlocked Cross-cutting)
//   xc-documents   → <Documents />   (S5.B — XC-DOC Vault, 1st unlocked Cross-cutting)
//   xc-messages    → <Messages />    (S6.B — XC-MSG, 2nd unlocked Cross-cutting)
//   xc-team        → <Team />        (S7.A — XC-TEAM, 3rd unlocked Cross-cutting)
//
//   After S10.B the dispatcher handles 4 stage sections + 6 cross-cutting
//   sections. The Layer 1 placeholder render path below is UNREACHABLE in
//   normal use; kept as safety net.
//
// DATA READ
//   Parcels query (Layer 1 safety net only): parcels where
//   workspaceId == currentWorkspaceId. First UI-driven query against the
//   migrated top-level parcels collection.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getVerticalLabel } from '../config/roles';
import Overview from './Overview';
import Portfolio from './Portfolio';
import Operate from './Operate';
import Maintain from './Maintain';

// ── S3.C SMOKE HARNESS imports (May 18, 2026) ────────────────────────────
// Wire the XC-DPUI shell component to the businessCase reference config.
// To remove the DPUI smoke harness: delete these two imports and the
// if-block at the top of PlaceholderDashboard() marked "S3.C smoke harness".
import DiscreteProcessUI from './DiscreteProcessUI';
import { PROCESS_CONFIGS } from './processConfigs';

// ── S4.C SMOKE HARNESS imports (May 18, 2026) ────────────────────────────
// Wire the XC-FILTER component for visual smoke. Renders the chip row
// in isolation behind ?xcfilter-smoke=1. To remove the XC-FILTER smoke
// harness: delete this import and the if-block at the top of
// PlaceholderDashboard() marked "S4.C smoke harness".
import XcFilterBar from './xcFilterBar';

// ── S5.B DISPATCH + SMOKE HARNESS imports (May 18, 2026) ─────────────────
// Documents.jsx serves two roles: (1) live dispatch target for the
// xc-documents section (first unlocked Cross-cutting nav item), and
// (2) ?xcdoc-smoke=1 render target with mockMode={true} for smoke
// testing before /documents collection has real data.
// To remove the xcdoc smoke harness only: delete the ?xcdoc-smoke if-block
// (keeps live dispatch intact). To remove the xc-documents live dispatch:
// delete the `if (section === 'xc-documents')` block (keeps smoke intact).
// To remove both: delete this import line as well.
import Documents from './Documents';

// ── S6.B DISPATCH + SMOKE HARNESS imports (May 19, 2026) ─────────────────
// Messages.jsx serves two roles: (1) live dispatch target for the
// xc-messages section (second unlocked Cross-cutting nav item), and
// (2) ?xcmsg-smoke=1 render target with mockMode={true} for smoke
// testing — uses 6-thread hardcoded mock array spanning all 4 status
// values (unread / replied / auto / review) per S6 close handoff §6c.
// Live mode reads flat /messages collection (Path Z architecture per
// AS-S6-001) and derives threads client-side via groupBy on phone-pair.
// To remove the xcmsg smoke harness only: delete the ?xcmsg-smoke if-block.
// To remove the xc-messages live dispatch: delete the corresponding
// `if (section === 'xc-messages')` block (keeps smoke intact).
// To remove both: delete this import line as well.
import Messages from './Messages';

// ── S7.A DISPATCH + SMOKE HARNESS imports (May 21, 2026) ─────────────────
// Team.jsx serves two roles: (1) live dispatch target for the
// xc-team section (third unlocked Cross-cutting nav item), and
// (2) ?xcteam-smoke=1 render target with mockMode={true} for smoke
// testing — uses 5-member hardcoded mock array on workspace a1 that
// exercises compound owner_pm role rendering (members appear in BOTH
// Owner and PM rows per Guardrail #3) and unassigned-role rendering
// for the 7 role slots without matching members.
// Live mode reads /clientMembers where workspaceId == X AND status ==
// 'active' (XC-ENT entity-level scope; parcel-level overrides deferred
// to PA-XC-ENT-AND-PARCEL-ROLES-SPEC). PM+ roles see full Team matrix;
// tenant/vendor render empty state with Messages cross-link (Guardrail
// #11). onSectionChange wires the Messages CTA in tenant/vendor state.
// To remove the xcteam smoke harness only: delete the ?xcteam-smoke if-block.
// To remove the xc-team live dispatch: delete the corresponding
// `if (section === 'xc-team')` block (keeps smoke intact).
// To remove both: delete this import line as well.
import Team from './Team';

// ── S7.B SMOKE HARNESS import (May 21, 2026) ─────────────────────────────
// RevPlanPanel.jsx — XC-REV side panel platform component. NOT a left-nav
// item — it's a side panel triggered from stage views (Operate.jsx wires
// the first trigger at S7.B; other stage triggers wire at Tier 3).
//
// Smoke harness ?revplan-smoke=1 renders the panel in MOCK MODE (panel
// stays open, no backdrop, no close button) so it can be visually inspected
// without needing to navigate into a stage view. Tests two-tab structure
// (Chapters + Ledger) and 7 chapter placeholders.
//
// To remove the harness only: delete the ?revplan-smoke if-block.
// To remove this import entirely: also delete the import below.
import RevPlanPanel from './RevPlanPanel';

// ── S8.B DISPATCH + SMOKE HARNESS import (Jun 29, 2026) ──────────────────
// Network.jsx serves two roles: (1) live dispatch target for the
// xc-network section (fourth unlocked Cross-cutting nav item), and
// (2) ?xcnet-smoke=1 render target with mockMode={true} for smoke
// testing — uses an 8-vendor hardcoded mock array spanning T1/T2/T3 that
// exercises tier grouping, the tier+category filter (XcFilterBar viewId
// "xc-network"), the Trustack rating-signals card, and the vendor table.
// Live mode reads the top-level /vendors collection platform-wide (rules
// v3.4: read = any signed-in; no workspaceId scoping — memory #4) and
// sorts client-side by tier then name (no composite index dependency at
// current volume). Canonical §6 vendor shape locked at S8.A.
// To remove the xcnet smoke harness only: delete the ?xcnet-smoke if-block.
// To remove the xc-network live dispatch: delete the corresponding
// `if (section === 'xc-network')` block (keeps smoke intact).
// To remove both: delete this import line as well.
import Network from './Network';

// ── S9.A DISPATCH + SMOKE HARNESS import (Jun 30, 2026) ──────────────────
// Marketplace.jsx serves two roles: (1) live dispatch target for the
// xc-marketplace section (fifth unlocked Cross-cutting nav item), and
// (2) ?xcmkt-smoke=1 render target with mockMode={true} for smoke
// testing — uses a 6-vendor hardcoded mock array spanning T1/T2/T3 that
// exercises the search form, the client-side matchVendors ranking (S8.F),
// T1/T2/T3 candidate cards, the tier post-filter (XcFilterBar viewId
// "xc-marketplace", already registered), and the T3 fallback band.
// Live mode reads the SAME top-level /vendors collection Network reads
// (platform-wide, rules v3.4). Marketplace is the transactional surface
// over that roster; Network is the relationship surface. No CF dependency
// in S9.A — the matchSubcontractors CF rewrite + server dispatch lands at
// S9.B, the job-attached invite (Hire CTA) at S9.D.
// To remove the xcmkt smoke harness only: delete the ?xcmkt-smoke if-block.
// To remove the xc-marketplace live dispatch: delete the corresponding
// `if (section === 'xc-marketplace')` block (keeps smoke intact).
// To remove both: delete this import line as well.
import Marketplace from './Marketplace';

// ── S10.B DISPATCH + SMOKE HARNESS import (Jul 8, 2026) ──────────────────
// Calendar.jsx serves two roles: (1) live dispatch target for the
// xc-calendar section (sixth unlocked Cross-cutting nav item), and
// (2) ?xccal-smoke=1 render target with mockMode={true} for smoke
// testing — uses an 8-event hardcoded mock array spanning event types
// that exercises the upcoming-events table (Date / Type / Parcel /
// Responsible Party / Days out / Action), the four KPI cards (Upcoming /
// Next 30 / Unassigned / iCal stub), the iCal read-only band, and the
// + New event form (disabled in mock).
// Live mode reads the top-level /events collection scoped to workspaceId
// (rules block + §6b schema live since S5; this is the FIRST write path
// onto /events). Query: where('workspaceId','==',X) orderBy startAt asc,
// with a soft-fallback retry without orderBy (missing composite index).
// Stage-agnostic (AS-S10-CAL-STAGE-AGNOSTIC) — Cross-cutting nav has no
// stage context, same as Network/Marketplace shipped. + New event writes
// a §6b doc client-side. Export iCal opens the calendarFeed CF (S10.C).
// To remove the xccal smoke harness only: delete the ?xccal-smoke if-block.
// To remove the xc-calendar live dispatch: delete the corresponding
// `if (section === 'xc-calendar')` block (keeps smoke intact).
// To remove both: delete this import line as well.
import Calendar from './Calendar';


// ── Section title map ────────────────────────────────────────────────────
const SECTION_TITLES = {
  overview:  'Overview',
  portfolio: 'Portfolio',
  operate:   'Operate',
  maintain:  'Maintain',
};

const SECTION_DESCRIPTIONS = {
  overview:  'High-level snapshot of this workspace.',
  portfolio: 'Properties and units in this workspace.',
  operate:   'Day-to-day operations: leases, bookings, communications.',
  maintain:  'Work orders, vendors, and maintenance activity.',
};

// ── Inline styles ────────────────────────────────────────────────────────

const styles = {
  container: {
    maxWidth: 1100,
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
  },
  sectionHeader: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#6B7280',
    margin: 0,
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    marginTop: 0,
    marginBottom: 12,
  },
  factGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
  },
  fact: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  factLabel: {
    fontSize: 12,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    fontWeight: 600,
  },
  factValue: {
    fontSize: 18,
    color: '#003060',
    fontWeight: 700,
  },
  parcelList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  parcelItem: {
    padding: '12px 16px',
    backgroundColor: '#F8F9FA',
    borderRadius: 6,
    border: '1px solid #E5E7EB',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 14,
    color: '#003060',
  },
  parcelMeta: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'monospace',
  },
  loadingState: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: 14,
    padding: '8px 0',
  },
  errorState: {
    color: '#B91C1C',
    fontSize: 13,
    padding: '12px 16px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: 6,
  },
  emptyState: {
    color: '#6B7280',
    fontSize: 14,
    fontStyle: 'italic',
  },
  footer: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#FEF3C7',
    border: '1px solid #FBBF24',
    borderRadius: 6,
    fontSize: 12,
    color: '#92400E',
    lineHeight: 1.5,
  },
  // ── S4.C smoke harness styles (May 18, 2026) ─────────────────────────
  smokeWrap: {
    maxWidth: 1100,
    margin: '24px auto',
    padding: '0 20px',
    fontFamily: 'Arial, sans-serif',
  },
  smokeBanner: {
    backgroundColor: '#F1EBFA',
    border: '1px solid rgba(110, 74, 160, 0.2)',
    borderRadius: 8,
    padding: '12px 18px',
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  smokeBannerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#3B2566',
  },
  smokeBannerTag: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 700,
    color: '#6E4AA0',
    background: 'rgba(110, 74, 160, 0.12)',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  smokeBannerMuted: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'monospace',
  },
  smokeTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  smokeSub: {
    fontSize: 14,
    color: '#6B7280',
    margin: 0,
    marginBottom: 18,
  },
  smokeFooter: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#FEF3C7',
    border: '1px solid #FBBF24',
    borderRadius: 6,
    fontSize: 12,
    color: '#92400E',
    lineHeight: 1.5,
  },
};

// ── Parcel list subcomponent ─────────────────────────────────────────────

function ParcelList({ workspaceId }) {
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadParcels() {
      if (!workspaceId) {
        setParcels([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const q = query(
          collection(db, 'parcels'),
          where('workspaceId', '==', workspaceId),
          orderBy('parcelId', 'asc')
        );
        const snap = await getDocs(q);

        if (cancelled) return;

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setParcels(list);
      } catch (err) {
        if (cancelled) return;
        console.warn('[PlaceholderDashboard] Parcels query failed, retrying without orderBy:', err.message);
        try {
          const q2 = query(
            collection(db, 'parcels'),
            where('workspaceId', '==', workspaceId)
          );
          const snap2 = await getDocs(q2);
          if (cancelled) return;
          const list2 = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
          setParcels(list2);
        } catch (err2) {
          if (cancelled) return;
          setError(err2.message || 'Failed to load parcels');
          setParcels([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadParcels();
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (loading) {
    return <div style={styles.loadingState}>Loading parcels…</div>;
  }

  if (error) {
    return (
      <div style={styles.errorState}>
        Could not load parcels: {error}
      </div>
    );
  }

  if (parcels.length === 0) {
    return (
      <div style={styles.emptyState}>
        No parcels in this workspace yet.
      </div>
    );
  }

  return (
    <ul style={styles.parcelList}>
      {parcels.map((p) => (
        <li key={p.id} style={styles.parcelItem}>
          <span>
            <strong>{p.displayName || p.parcelId || p.id}</strong>
            {p.address && (
              <span style={{ color: '#6B7280', marginLeft: 8, fontSize: 13 }}>
                — {p.address}
              </span>
            )}
          </span>
          <span style={styles.parcelMeta}>
            {p.lifecyclePhase || '—'}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Main component ───────────────────────────────────────────────────────

export default function PlaceholderDashboard({ section, workspace, roles, roleLabel, onSectionChange }) {
  // ── S3.C smoke harness (XC-DPUI) ──────────────────────────────────────
  // Opt-in via URL query param ?dpui-smoke=1. When present, render the
  // XC-DPUI shell component with the businessCase reference config and
  // return early — bypasses all section dispatch below. When absent, this
  // block is a no-op and the rest of the component runs unchanged.
  //
  // Reads window.location.search directly (rather than using react-router's
  // useSearchParams hook) to avoid coupling this smoke harness to routing
  // and to keep removal trivial when S3 closes.
  //
  // To remove: delete this if-block and the two imports at the top
  // (DiscreteProcessUI, PROCESS_CONFIGS).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dpui-smoke') === '1') {
      return (
        <DiscreteProcessUI config={PROCESS_CONFIGS.businessCase} />
      );
    }
  }

  // ── S4.C smoke harness (XC-FILTER) ────────────────────────────────────
  // Opt-in via URL query param ?xcfilter-smoke=1. When present, render
  // the XC-FILTER component (XcFilterBar) in isolation with a dev-only
  // visual frame and return early. Renders the 'opr-props' filter
  // configuration (Properties view: Entity / Type / Status) which has
  // three fields and exercises the chip-row layout, active-state styling,
  // and reset-button visibility.
  //
  // To remove: delete this if-block and the XcFilterBar import at the top.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xcfilter-smoke') === '1') {
      const handleChange = (values) => {
        // eslint-disable-next-line no-console
        console.log('[XcFilterBar smoke] values changed:', values);
      };
      return (
        <div style={styles.smokeWrap}>
          <div style={styles.smokeBanner}>
            <div>
              <span style={styles.smokeBannerTitle}>XC-FILTER Component</span>
              <span style={styles.smokeBannerTag}>Platform-Level Architecture</span>
            </div>
            <div style={styles.smokeBannerMuted}>
              One component · 21 viewId configurations · embedded per-view
            </div>
          </div>

          <h1 style={styles.smokeTitle}>XC-FILTER Filter Bar</h1>
          <p style={styles.smokeSub}>
            S4 smoke render. The filter chip row below uses the
            <code style={{ margin: '0 4px', fontFamily: 'Consolas, monospace', fontSize: 12 }}>opr-props</code>
            viewId — three fields (Entity, Type, Status). Changing any
            field switches the chip to cyan and reveals the Reset button.
            All changes logged to the console.
          </p>

          <XcFilterBar viewId="opr-props" onChange={handleChange} />

          <div style={styles.smokeFooter}>
            <strong>S4 smoke render.</strong> XC-FILTER component live. Pure
            presentation — no Firestore reads, no consumers wired. First
            production consumer lands in a later session (XC-DOC list view
            at S5 or XC-NET vendor list at S8, whichever ships first).
          </div>
        </div>
      );
    }
  }

  // ── S5.B smoke harness (XC-DOC) ───────────────────────────────────────
  // Opt-in via URL query param ?xcdoc-smoke=1. When present, render
  // Documents.jsx with mockMode={true} — bypasses Firestore reads and
  // uses hardcoded 4-doc mock array spanning stages. Used to smoke-test
  // the Vault UI before /documents collection has real data, AND to
  // verify the component renders correctly for any signed-in user
  // regardless of workspace state.
  //
  // To remove: delete this if-block. Documents import stays for live
  // dispatch (xc-documents section below).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xcdoc-smoke') === '1') {
      return (
        <Documents
          workspace={workspace}
          roles={roles}
          roleLabel={roleLabel}
          mockMode={true}
        />
      );
    }
  }

  // ── S6.B smoke harness (XC-MSG) ───────────────────────────────────────
  // Opt-in via URL query param ?xcmsg-smoke=1. When present, render
  // Messages.jsx with mockMode={true} — bypasses Firestore reads and
  // uses hardcoded 6-thread mock array spanning all 4 status states
  // (unread / replied / auto / review per S6 close handoff §6c). Used
  // to smoke-test the thread list UI before /messages collection has
  // real data, AND to verify the component renders correctly for any
  // signed-in user regardless of workspace state.
  //
  // To remove: delete this if-block. Messages import stays for live
  // dispatch (xc-messages section below).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xcmsg-smoke') === '1') {
      return (
        <Messages
          workspace={workspace}
          roles={roles}
          roleLabel={roleLabel}
          mockMode={true}
        />
      );
    }
  }

  // ── S7.A smoke harness (XC-TEAM) ──────────────────────────────────────
  // Opt-in via URL query param ?xcteam-smoke=1. When present, render
  // Team.jsx with mockMode={true} — bypasses Firestore reads and uses
  // hardcoded 5-member mock array on workspace a1 that exercises:
  //   - Two owner_pm members → both appear in Owner row AND PM row
  //     (Guardrail #3: compound role rendering)
  //   - One pm_only member → appears only in PM row
  //   - One vendor + one sub → appear in Vendor and Subcontractor rows
  //   - 4 other role slots (CM, Maintenance, Tenant Coordinator, Legal,
  //     Bookkeeper) → render "Unassigned · Go to Marketplace" buttons
  // Used to smoke-test the role matrix UI before /clientMembers has real
  // data, AND to verify the component renders correctly for any signed-in
  // user regardless of workspace state. Mock mode bypasses the PM+ access
  // check (Guardrail #11) so the matrix renders regardless of caller role.
  //
  // To remove: delete this if-block. Team import stays for live
  // dispatch (xc-team section below).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xcteam-smoke') === '1') {
      return (
        <Team
          workspace={workspace}
          roles={roles}
          roleLabel={roleLabel}
          onSectionChange={onSectionChange}
          mockMode={true}
        />
      );
    }
  }

  // ── S7.B smoke harness (XC-REV / RevPlanPanel) ─────────────────────────
  // Opt-in via URL query param ?revplan-smoke=1. When present, render
  // RevPlanPanel.jsx with mockMode={true} — panel stays open, no backdrop,
  // no close button. Used to smoke-test the two-tab structure (Chapters +
  // Ledger) and 7 chapter placeholders without needing to navigate into
  // Operate stage and click the RevPlan trigger button.
  //
  // Live mode: panel triggers from Operate.jsx (S7.B wires first trigger);
  // other stage triggers wire at Tier 3 as discrete processes start firing
  // chapter-write gate events.
  //
  // To remove: delete this if-block. RevPlanPanel import stays for live
  // use from Operate.jsx and future stage triggers.
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('revplan-smoke') === '1') {
      return (
        <div style={{
          maxWidth: 1100,
          margin: '0 auto',
          padding: '24px 20px',
          fontFamily: 'Arial, sans-serif',
        }}>
          <div style={{
            backgroundColor: '#F1EBFA',
            border: '1px solid rgba(110, 74, 160, 0.2)',
            borderRadius: 8,
            padding: '12px 18px',
            marginBottom: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}>
            <div>
              <span style={{
                fontSize: 13,
                fontWeight: 700,
                color: '#3B2566',
              }}>RevPlan Side Panel</span>
              <span style={{
                display: 'inline-block',
                marginLeft: 8,
                padding: '2px 8px',
                fontSize: 10,
                fontWeight: 700,
                color: '#6E4AA0',
                background: 'rgba(110, 74, 160, 0.12)',
                borderRadius: 10,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}>XC-REV · S7 smoke</span>
            </div>
            <div style={{
              fontSize: 12,
              color: '#6B7280',
              fontFamily: 'monospace',
            }}>
              Mock mode · 7 chapter placeholders · Ledger tab stub
            </div>
          </div>

          <h1 style={{
            fontSize: 24,
            fontWeight: 700,
            color: '#003060',
            margin: 0,
            marginBottom: 4,
          }}>RevPlanPanel</h1>
          <p style={{
            fontSize: 14,
            color: '#6B7280',
            margin: 0,
            marginBottom: 18,
          }}>
            S7 smoke render. Panel renders inline below in mock mode (no
            backdrop, no close button). Click between "Chapters" and "Ledger"
            tabs. Operate chapter should be highlighted as "Current" when
            stage=operate is passed.
          </p>

          <div style={{
            position: 'relative',
            minHeight: 600,
            display: 'flex',
            justifyContent: 'center',
          }}>
            <RevPlanPanel
              workspace={workspace}
              isOpen={true}
              onClose={() => {}}
              stage="operate"
              mockMode={true}
            />
          </div>
        </div>
      );
    }
  }

  // ── S8.B smoke harness (XC-NET / Network) ─────────────────────────────
  // Opt-in via URL query param ?xcnet-smoke=1. When present, render
  // Network.jsx with mockMode={true} — bypasses the /vendors Firestore
  // read and uses an 8-vendor hardcoded mock array spanning T1/T2/T3.
  // Exercises: tier grouping + sort, the tier+category filter bar
  // (XcFilterBar viewId "xc-network"), the Trustack rating-signals card,
  // KPI tier counts, and the vendor table with COI/W-9 status badges.
  // Used to smoke-test the Network surface independently of roster state.
  //
  // To remove: delete this if-block. Network import stays for live
  // dispatch (xc-network section below).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xcnet-smoke') === '1') {
      return (
        <Network
          workspace={workspace}
          roles={roles}
          roleLabel={roleLabel}
          mockMode={true}
        />
      );
    }
  }

  // ── S9.A smoke harness (XC-MKT / Marketplace) ─────────────────────────
  // Opt-in via URL query param ?xcmkt-smoke=1. When present, render
  // Marketplace.jsx with mockMode={true} — bypasses the /vendors Firestore
  // read and uses a 6-vendor hardcoded mock array spanning T1/T2/T3.
  // Exercises: the search form (job-category select derived from roster
  // trades + area input), the client-side matchVendors ranking (S8.F),
  // T1/T2/T3 candidate cards with score + reasons, the tier post-filter
  // (XcFilterBar viewId "xc-marketplace"), the Materials deferred state,
  // and the T3 fallback band when no match is found. Used to smoke-test
  // the Marketplace surface independently of roster state.
  //
  // To remove: delete this if-block. Marketplace import stays for live
  // dispatch (xc-marketplace section below).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xcmkt-smoke') === '1') {
      return (
        <Marketplace
          workspace={workspace}
          roles={roles}
          roleLabel={roleLabel}
          mockMode={true}
        />
      );
    }
  }

  // ── S10.B smoke harness (XC-CAL / Calendar) ───────────────────────────
  // Opt-in via URL query param ?xccal-smoke=1. When present, render
  // Calendar.jsx with mockMode={true} — bypasses the /events Firestore
  // read and the write path, and uses an 8-event hardcoded mock array
  // spanning event types. Exercises: the upcoming-events table (Date /
  // Type / Parcel / Responsible Party / Days out / Action), the four KPI
  // cards (Upcoming / Next 30 days / Unassigned / iCal subscribers stub),
  // the iCal read-only band, and the + New event form (disabled in mock).
  // Used to smoke-test the Calendar surface independently of /events state.
  //
  // To remove: delete this if-block. Calendar import stays for live
  // dispatch (xc-calendar section below).
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('xccal-smoke') === '1') {
      return (
        <Calendar
          workspace={workspace}
          roles={roles}
          roleLabel={roleLabel}
          mockMode={true}
        />
      );
    }
  }

  // ── S32 Layer 2 Overview dispatch ──────────────────────────────────────
  if (section === 'overview') {
    return <Overview workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S33 Layer 2 Portfolio dispatch ─────────────────────────────────────
  if (section === 'portfolio') {
    return <Portfolio workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S34 Layer 2 Operate-A dispatch ─────────────────────────────────────
  // AS-CC Option B: internal STR | LTR top tabs inside Operate.jsx.
  // STR = read-only bookings (BookingCalendar + BookingDetail absorption).
  // LTR = 6 sub-tabs (Applications, Leases, Deposits, Inspections, Rent, Showings).
  // S35 added Messages + Assets tabs (4 → 6 → 4 final structure).
  // All writes stub as toasts (AS-DD). Showings is placeholder per AS-EE.
  if (section === 'operate') {
    return <Operate workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S36 Layer 2 Maintain dispatch ──────────────────────────────────────
  // 7 internal top tabs: Overview | Work Orders | Board | Network |
  // Vendors | Messages | Assets. Absorbs 4 PMApp routes plus AS-PP/AS-QQ
  // duplications. Read-only Layer 2 (AS-DD); writes are toast stubs (AS-Q).
  if (section === 'maintain') {
    return <Maintain workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S8.B XC-NET dispatch (Cross-cutting, Jun 29, 2026) ─────────────────
  // Fourth unlocked Cross-cutting nav item. ShellNav xc-network row has
  // layer1Active=true (S8.B flip); click routes here. Network.jsx reads
  // the top-level /vendors collection platform-wide (rules v3.4: read =
  // any signed-in member; no workspaceId scoping — memory #4 reputation
  // is platform-wide). Renders the XC-NET relationship surface: banner,
  // T1/T2/T3 KPI grid, tier+category filter (XcFilterBar viewId
  // "xc-network", already registered), Trustack rating-signals card, and
  // the vendor roster table. Canonical §6 vendor shape locked at S8.A.
  // Vendor profile detail (onViewProfile) wires at S8.C; until then the
  // row "View" action is a no-op stub. Stage-scoped filtering deferred
  // (AS-S8-NET-NOT-STAGE-SCOPED) — Cross-cutting nav has no stage context.
  if (section === 'xc-network') {
    return <Network workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S9.A XC-MKT dispatch (Cross-cutting, Jun 30, 2026) ─────────────────
  // Fifth unlocked Cross-cutting nav item. ShellNav xc-marketplace row has
  // layer1Active=true (S9.A flip); click routes here. Marketplace.jsx reads
  // the SAME top-level /vendors collection Network reads (platform-wide,
  // rules v3.4). It is the TRANSACTIONAL surface over that roster — "who
  // do I need RIGHT NOW for THIS job?" — vs Network's relationship surface.
  // Renders: banner, T1→T2→T3 explainer, dormant KPI grid (no fires
  // collection yet — AS-S9-MKT-KPI-DORMANT), tier+category filter
  // (XcFilterBar viewId "xc-marketplace", already registered), and a live
  // search that ranks candidates via the client-side matchVendors matcher
  // (S8.F) with T1/T2/T3 cards. NO CF dependency in S9.A. The server-side
  // matchSubcontractors dispatch rewrite lands S9.B; the job-attached
  // invite (Hire CTA, rendered disabled here) lands S9.D. Stage-agnostic
  // (AS-S9-MKT-STAGE-AGNOSTIC) — Cross-cutting nav has no stage context,
  // same as Network shipped. Materials category routes to a deferred state
  // (workOrders dep, S13).
  if (section === 'xc-marketplace') {
    return <Marketplace workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S10.B XC-CAL dispatch (Cross-cutting, Jul 8, 2026) ─────────────────
  // Sixth unlocked Cross-cutting nav item. ShellNav xc-calendar row has
  // layer1Active=true (S10.B flip); click routes here. Calendar.jsx reads
  // the top-level /events collection scoped to workspaceId (rules block +
  // §6b canonical schema live since S5; this session is the FIRST write
  // path onto /events). Query: where('workspaceId','==',X) orderBy startAt
  // asc, with a soft-fallback retry without orderBy (missing composite
  // index — one-click create, AS-S10-INDEX). Renders the universal events
  // surface: banner, page head, iCal read-only band (feed = calendarFeed
  // CF, S10.C), + New event inline form (writes a §6b doc client-side),
  // XcFilterBar (viewId "xc-calendar"; values not consumed per AS-S5-013),
  // four KPI cards, and the upcoming-events table. Stage-agnostic
  // (AS-S10-CAL-STAGE-AGNOSTIC) — Cross-cutting nav has no stage context,
  // same as Network/Marketplace shipped. Export iCal opens the read-only
  // feed for this workspace.
  if (section === 'xc-calendar') {
    return <Calendar workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S5.B XC-DOC dispatch (Cross-cutting, May 18, 2026) ─────────────────
  // First unlocked Cross-cutting nav item. ShellNav xc-documents row has
  // layer1Active=true (S5.B flip); click routes here. Documents.jsx reads
  // the /documents top-level collection scoped to workspaceId per S2 v3.3
  // rules. Upload functional: Firebase Storage put → /documents write →
  // ocrDocument CF (AI-08) invoke. AI-26 deferred per AS-S5-004.
  if (section === 'xc-documents') {
    return <Documents workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S6.B XC-MSG dispatch (Cross-cutting, May 19, 2026) ────────────────
  // Second unlocked Cross-cutting nav item. ShellNav xc-messages row has
  // layer1Active=true (S6.B flip); click routes here. Messages.jsx reads
  // the flat /messages top-level collection (Path Z architecture per
  // AS-S6-001) and derives threads client-side via groupBy on phone-pair
  // per S6 close handoff §6c rules. Compose flow calls
  // httpsCallable("sendSms") — sendSms PascalCase role bug fixed at S6.C.
  // AI-01 draft assist via httpsCallable("draftGuestComm").
  if (section === 'xc-messages') {
    return <Messages workspace={workspace} roles={roles} roleLabel={roleLabel} />;
  }

  // ── S7.A XC-TEAM dispatch (Cross-cutting, May 21, 2026) ───────────────
  // Third unlocked Cross-cutting nav item. ShellNav xc-team row has
  // layer1Active=true (S7.A flip); click routes here. Team.jsx reads
  // /clientMembers where workspaceId == X AND status == 'active' (entity-
  // level scope; parcel-level overrides deferred to PA-XC-ENT-AND-PARCEL-
  // ROLES-SPEC). Renders 9 conceptual role rows. Compound owner_pm members
  // render in BOTH Owner and PM rows (Guardrail #3). PM+ roles see the
  // full Team matrix; tenant/vendor render empty state with Messages
  // cross-link (Guardrail #11). onSectionChange wires the Messages CTA.
  // Unassigned-role clicks fire S7.D toast (Marketplace stub, S8).
  // Composite index required: clientMembers · workspaceId + status +
  // __name__ (must be added in Firebase Console before first live query).
  if (section === 'xc-team') {
    return (
      <Team
        workspace={workspace}
        roles={roles}
        roleLabel={roleLabel}
        onSectionChange={onSectionChange}
      />
    );
  }

  // ── Layer 1 placeholder (unreachable post-S36; kept as safety net) ─────
  const sectionTitle = SECTION_TITLES[section] || 'Section';
  const sectionDescription = SECTION_DESCRIPTIONS[section] || '';

  if (!workspace) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading workspace…</div>
      </div>
    );
  }

  const verticalLabel = workspace.verticalType
    ? getVerticalLabel(workspace.verticalType)
    : 'Property Management';

  return (
    <div style={styles.container}>
      {/* Section header */}
      <header style={styles.sectionHeader}>
        <h1 style={styles.sectionTitle}>{sectionTitle}</h1>
        <p style={styles.sectionDescription}>{sectionDescription}</p>
      </header>

      {/* Workspace summary card */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Workspace</h2>
        <div style={styles.factGrid}>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Name</span>
            <span style={styles.factValue}>{workspace.workspaceName || '—'}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Vertical</span>
            <span style={styles.factValue}>{verticalLabel}</span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Plan</span>
            <span style={styles.factValue}>
              {workspace.plan ? workspace.plan : '—'}
            </span>
          </div>
          <div style={styles.fact}>
            <span style={styles.factLabel}>Your role</span>
            <span style={styles.factValue}>{roleLabel || '—'}</span>
          </div>
        </div>
      </section>

      {/* Parcels card — live data read from new schema */}
      <section style={styles.card}>
        <h2 style={styles.cardHeader}>Parcels in this workspace</h2>
        <ParcelList workspaceId={workspace.workspaceId} />
      </section>

      {/* Layer 1 footer note */}
      <div style={styles.footer}>
        <strong>Layer 1 placeholder.</strong> Real {sectionTitle.toLowerCase()}{' '}
        content lands in a future release as legacy app components are
        absorbed into role-scoped views inside this shell. The data above is
        a live read from the migrated database — confirming the auth +
        workspace + membership + parcels read path is working end-to-end.
      </div>
    </div>
  );
}
